import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import history from "../../utils/history";
import urls from "../../global/constants/UrlConstants";
import YhTable, { YhTableCellParams, YhTableColumn } from "../../ui/YhTable";
import YhInput from "../../ui/YhInput";
import YhButton from "../../ui/YhButton";
import YhSelect from "../../ui/YhSelect";
import CustomLoader from "../../global/components/CustomLoader/CustomLoader";
import {
  OrgNameCell,
  useOrgNameMap,
} from "../../Shared/Organization/OrganizationSelect";
import {
  selectActiveOrganizationId,
  selectMemberships,
} from "../../redux/authSlice";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "../../helpers/methods";
import notifiers from "../../global/constants/NotificationConstants";
import {
  deleteScheduledWhatsAppBroadcast,
  listWhatsAppBroadcasts,
} from "../../services/whatsappMarketingService";
import { listTenantOrganizations } from "../../services/organizationService";
import { getProjectDetails } from "../Project/components/ShowAll/ShowAllService";
import MarketingAccessGate from "./MarketingAccessGate";

const statusTone: Record<string, string> = {
  queued: "bg-[#F9F0F4] text-[#4338CA]",
  scheduled: "bg-[#F9F0F4] text-[#3730A3]",
  processing: "bg-[#FEF3C7] text-[#B45309]",
  completed: "bg-[#DCFCE7] text-[#15803D]",
  partial: "bg-[#FFEDD5] text-[#C2410C]",
  failed: "bg-[#FEE2E2] text-[#B91C1C]",
  cancelled: "bg-[#F3F4F6] text-[#4B5563]",
};

const FilterField = ({ children }: { children: React.ReactNode }) => (
  <div className="flex min-h-[76px] w-full min-w-0 flex-col justify-end">
    {children}
  </div>
);

const WhatsAppMarketingPage = () => {
  const orgNameMap = useOrgNameMap();
  const activeOrgId = useSelector(selectActiveOrganizationId);
  const memberships = useSelector(selectMemberships);
  const [organizationId, setOrganizationId] = useState(
    activeOrgId && activeOrgId !== "all" ? String(activeOrgId) : ""
  );
  const [orgOptions, setOrgOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [projectId, setProjectId] = useState("");
  const [projects, setProjects] = useState<{ label: string; value: string }[]>(
    []
  );
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [metaData, setMetaData] = useState<any>({});
  const [deletingId, setDeletingId] = useState<string>("");

  const loadBroadcasts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listWhatsAppBroadcasts({
        organizationId: organizationId || undefined,
        projectId: projectId || undefined,
        search,
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
      });
      setRows(Array.isArray(res?.data) ? res.data : []);
      setMetaData(res?.metadata?.[0] || {});
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error?.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setLoading(false);
    }
  }, [organizationId, projectId, search, paginationModel]);

  useEffect(() => {
    loadBroadcasts();
  }, [loadBroadcasts]);

  const onDeleteScheduled = async (row: any) => {
    const id = String(row?._id || "");
    if (!id || row?.status !== "scheduled") return;
    const ok = window.confirm(
      `Delete scheduled broadcast "${row.name || id}"?\n\nMessages will not be sent. Completed broadcasts cannot be deleted.`
    );
    if (!ok) return;
    setDeletingId(id);
    try {
      const res = await deleteScheduledWhatsAppBroadcast(id);
      if (!res?.success) {
        openErrorNotification(res?.message || "Could not delete schedule");
        return;
      }
      openSuccessNotification(
        res.message || "Scheduled broadcast deleted — will not send"
      );
      await loadBroadcasts();
    } catch (e: any) {
      openErrorNotification(e?.message || "Could not delete schedule");
    } finally {
      setDeletingId("");
    }
  };
  useEffect(() => {
    if (activeOrgId && activeOrgId !== "all" && activeOrgId !== "*") {
      setOrganizationId(String(activeOrgId));
    } else if (activeOrgId === "all" || activeOrgId === "*") {
      setOrganizationId("");
    }
  }, [activeOrgId]);

  useEffect(() => {
    (async () => {
      try {
        const res = await listTenantOrganizations();
        const list = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.items)
          ? res.data.items
          : [];
        if (list.length) {
          setOrgOptions(
            list
              .map((o: any) => {
                const id = String(o._id || o.id || "");
                if (!id) return null;
                return {
                  value: id,
                  label: o.name || o.brandName || o.legalName || o.slug || id,
                };
              })
              .filter(Boolean) as { label: string; value: string }[]
          );
          return;
        }
      } catch {
        /* fall through */
      }
      const fromMem = (memberships || [])
        .map((m: any) => {
          const id = String(m?.organizationId || m?.organization?._id || "");
          if (!id) return null;
          return {
            value: id,
            label:
              m?.organization?.name ||
              m?.organization?.brandName ||
              m?.organizationName ||
              id,
          };
        })
        .filter(Boolean) as { label: string; value: string }[];
      const seen = new Set<string>();
      setOrgOptions(
        fromMem.filter((o) => {
          if (seen.has(o.value)) return false;
          seen.add(o.value);
          return true;
        })
      );
    })();
  }, [memberships]);

  useEffect(() => {
    (async () => {
      try {
        const res = await getProjectDetails({
          pageNumber: 0,
          pageSize: 200,
          search: "",
          status: 1,
        });
        const list = Array.isArray(res?.data) ? res.data : [];
        setProjects(
          list.map((p: any) => ({
            value: String(p._id),
            label: p.project_name || p.name || String(p._id),
          }))
        );
      } catch {
        setProjects([]);
      }
    })();
  }, []);

  const columns: YhTableColumn[] = [
    {
      headerName: "Broadcast",
      field: "name",
      minWidth: 200,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-sm font-semibold text-[#111827]">
            {params.row?.name || "—"}
          </span>
          <span className="truncate text-xs text-[#6B7280]">
            {params.row?.templateName || params.row?.templateWid || "—"}
          </span>
        </div>
      ),
    },
    {
      headerName: "Organization",
      field: "organizationId",
      minWidth: 150,
      renderCell: (params: YhTableCellParams) => (
        <OrgNameCell
          organizationId={params.row?.organizationId}
          map={orgNameMap}
        />
      ),
    },
    {
      headerName: "Project",
      field: "projectName",
      minWidth: 140,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-[13px] text-[#374151]">
          {params.row?.projectName || (params.row?.projectId ? "—" : "All")}
        </span>
      ),
    },
    {
      headerName: "Progress",
      field: "progress",
      minWidth: 180,
      renderCell: (params: YhTableCellParams) => {
        const percent = Number(params.row?.progress?.percent || 0);
        const totals = params.row?.totals || {};
        return (
          <div className="flex w-full min-w-[140px] flex-col gap-1">
            <div className="h-2 w-full overflow-hidden rounded-full bg-[#E5E7EB]">
              <div
                className="h-full rounded-full bg-[#A02448] transition-all"
                style={{ width: `${Math.min(100, percent)}%` }}
              />
            </div>
            <span className="text-[11px] text-[#6B7280]">
              {totals.sent || 0} sent · {totals.failed || 0} failed ·{" "}
              {totals.total || 0} total
            </span>
          </div>
        );
      },
    },
    {
      headerName: "Status",
      field: "status",
      minWidth: 120,
      renderCell: (params: YhTableCellParams) => {
        const st = String(params.row?.status || "");
        return (
          <span
            className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize ${
              statusTone[st] || "bg-[#F3F4F6] text-[#4B5563]"
            }`}
          >
            {st || "—"}
          </span>
        );
      },
    },
    {
      headerName: "Created",
      field: "createdAt",
      minWidth: 140,
      renderCell: (params: YhTableCellParams) => (
        <div className="flex min-w-0 flex-col">
          <span className="text-[13px] text-[#374151]">
            {params.row?.createdAt
              ? new Date(params.row.createdAt).toLocaleString("en-IN", {
                  timeZone: "Asia/Kolkata",
                })
              : "—"}
          </span>
          {params.row?.scheduledAt ? (
            <span className="text-[10px] font-medium text-[#4338CA]">
              Runs{" "}
              {new Date(params.row.scheduledAt).toLocaleString("en-IN", {
                timeZone: "Asia/Kolkata",
              })}{" "}
              IST
            </span>
          ) : null}
        </div>
      ),
    },
    {
      headerName: "Action",
      field: "action",
      minWidth: 150,
      width: 170,
      renderCell: (params: YhTableCellParams) => {
        const isScheduled = params.row?.status === "scheduled";
        const id = String(params.row._id);
        return (
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="text-sm font-semibold text-[#A02448] hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                history.push(
                  urls.whatsappMarketingDetailPath.replace(":id", id)
                );
              }}
            >
              View
            </button>
            {isScheduled ? (
              <button
                type="button"
                className="text-sm font-semibold text-[#B91C1C] hover:underline disabled:opacity-50"
                disabled={deletingId === id}
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteScheduled(params.row);
                }}
              >
                {deletingId === id ? "…" : "Delete"}
              </button>
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <MarketingAccessGate>
    <div className="flex h-full min-h-0 flex-col gap-5 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight text-[#111827]">
            WhatsApp Marketing
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            Create Engage template broadcasts with background processing and
            delivery reports.
          </p>
        </div>
        <YhButton
          variant="primary"
          onClick={() => history.push(urls.whatsappMarketingCreatePath)}
        >
          New broadcast
        </YhButton>
      </div>

      <div className="rounded-2xl border border-[#EEF0F5] bg-white p-4">
        <div className="grid grid-cols-1 gap-x-4 gap-y-1 md:grid-cols-3">
          <FilterField>
            <YhSelect
              label="Organization"
              value={organizationId}
              clearable
              placeholder="All organizations"
              onChange={(e: any) => {
                setOrganizationId(e.target.value || "");
                setPaginationModel((p) => ({ ...p, page: 0 }));
              }}
              options={[
                { label: "All organizations", value: "" },
                ...orgOptions,
              ]}
            />
          </FilterField>
          <FilterField>
            <YhSelect
              label="Project"
              value={projectId}
              clearable
              placeholder="All projects"
              onChange={(e: any) => {
                setProjectId(e.target.value || "");
                setPaginationModel((p) => ({ ...p, page: 0 }));
              }}
              options={[{ label: "All projects", value: "" }, ...projects]}
            />
          </FilterField>
          <FilterField>
            <YhInput
              label="Search"
              value={search}
              onChange={(e: any) => {
                setSearch(e.target.value);
                setPaginationModel((p) => ({ ...p, page: 0 }));
              }}
              placeholder="Broadcast or template…"
            />
          </FilterField>
        </div>
      </div>

      <div className="relative min-h-0 flex-1">
        <YhTable
          columns={columns}
          rows={rows}
          getRowId={(row) => String(row._id)}
          loading={loading}
          emptyMessage="No broadcasts yet"
          className="h-full min-h-[280px]"
          page={paginationModel.page}
          pageSize={paginationModel.pageSize}
          totalRows={metaData?.total || 0}
          paginationLabel="broadcasts"
          onPageChange={(p) =>
            setPaginationModel((prev) => ({ ...prev, page: p }))
          }
          onPageSizeChange={(s) =>
            setPaginationModel({ pageSize: s, page: 0 })
          }
          onRowClick={(row) =>
            history.push(
              urls.whatsappMarketingDetailPath.replace(":id", String(row._id))
            )
          }
        />
        {loading ? <CustomLoader apiResponse={false} /> : null}
      </div>
    </div>
    </MarketingAccessGate>
  );
};

export default WhatsAppMarketingPage;
